import React, { createContext, useContext, useEffect, useRef, ReactNode, useCallback } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { getFunnelVariant } from '../config/funnelV2';

// Event names - consolidated to 8 core events
export const AMPLITUDE_EVENTS = {
    STEP_VIEW: 'Onboarding Step View',
    STEP_COMPLETED: 'Onboarding Step Completed',
    LEAD: 'Lead',
    PAYWALL_VIEW: 'Paywall View',
    CHECKOUT_STARTED: 'Checkout Started',
    PAYMENT_SUBMITTED: 'Payment Submitted',
    TRIAL_PURCHASE: 'Trial Purchase',
    UPSELL_PURCHASE: 'Upsell Purchase',
    FINAL_SCREEN: 'Onboarding Final Screen',
} as const;

// Flow types
export type FlowType = 'b2b' | 'member';

// Step view properties
interface StepViewProps {
    step_name: string;
    step_index: number;
    total_steps: number;
    flow_type: FlowType;
}

// Step completed properties
interface StepCompletedProps {
    step_name: string;
    flow_type: FlowType;
    time_spent_seconds?: number;
    selections?: Record<string, any>;
}

// Lead properties
interface LeadProps {
    email: string;
    flow_type: FlowType;
    referral_code?: string;
}

// Purchase properties
interface PurchaseProps {
    plan_type?: string;
    plan_id?: string;
    plan_name?: string;
    price?: number;
    currency?: string;
    flow_type: FlowType;
    trial_promo?: string;
    plan_monthly_price?: number;
}

type PaymentMethodType = 'card' | 'invoice' | 'apple_pay' | 'google_pay' | 'link' | 'express_checkout';

interface PaymentSubmittedProps extends PurchaseProps {
    payment_method: PaymentMethodType;
}

// Upsell properties
interface UpsellProps {
    product_type: '1:1_coaching' | 'plan_upgrade' | string;
    price?: number;
    flow_type: FlowType;
}

interface AmplitudeContextType {
    // Core tracking methods (new consolidated API)
    trackStepView: (props: StepViewProps) => void;
    trackStepCompleted: (props: StepCompletedProps) => void;
    trackLead: (props: LeadProps) => void;
    trackPaywallView: (flowType: FlowType) => void;
    trackCheckoutStarted: (props: PurchaseProps) => void;
    trackPaymentSubmitted: (props: PaymentSubmittedProps) => void;
    trackTrialPurchase: (props: PurchaseProps) => void;
    trackUpsellPurchase: (props: UpsellProps) => void;
    trackFinalScreen: (flowType: FlowType) => void;

    // User identification
    identifyUser: (email: string, properties?: Record<string, any>) => void;
    setUserProperties: (properties: Record<string, any>) => void;
    resetUser: () => void;

    // Legacy support (for backwards compatibility)
    trackEvent: (eventName: string, eventProperties?: Record<string, any>) => void;
    trackPageView: (pageName: string, properties?: Record<string, any>) => void;
    setUserId: (userId: string) => void;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(undefined);

interface AmplitudeProviderProps {
    apiKey: string;
    children: ReactNode;
}

export const AmplitudeProvider: React.FC<AmplitudeProviderProps> = ({ apiKey, children }) => {
    const isInitialized = useRef(false);
    const lastTrackedEvent = useRef<string>('');
    const lastTrackedTime = useRef<number>(0);
    const sessionId = useRef<string>('');

    useEffect(() => {
        if (!apiKey || isInitialized.current) return;

        // Generate session ID for deduplication
        sessionId.current = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        amplitude.init(apiKey, undefined, {
            defaultTracking: {
                sessions: true,
                pageViews: false,
                formInteractions: false,
                fileDownloads: false,
            },
            attribution: {
                disabled: true,
            },
        } as any);

        isInitialized.current = true;
        console.log('[Amplitude] Initialized with session:', sessionId.current);

        const identify = new amplitude.Identify();
        identify.set('funnel_variant', getFunnelVariant());
        amplitude.identify(identify);
    }, [apiKey]);

    // Deduplication helper - prevents same event firing within 500ms
    const shouldTrackEvent = useCallback((eventKey: string): boolean => {
        const now = Date.now();
        const timeSinceLastTrack = now - lastTrackedTime.current;

        if (lastTrackedEvent.current === eventKey && timeSinceLastTrack < 500) {
            console.log('[Amplitude] Skipping duplicate event:', eventKey);
            return false;
        }

        lastTrackedEvent.current = eventKey;
        lastTrackedTime.current = now;
        return true;
    }, []);

    // Track event with deduplication
    const trackEventInternal = useCallback((eventName: string, properties: Record<string, any> = {}) => {
        if (!apiKey) return;

        const eventKey = `${eventName}-${JSON.stringify(properties)}`;
        if (!shouldTrackEvent(eventKey)) return;

        const funnelVariant = getFunnelVariant();
        const enrichedProps = {
            ...properties,
            funnel_variant: funnelVariant,
            funnel_v2: funnelVariant === 'v2',
            source: 'webfunnel',
            session_id: sessionId.current,
            timestamp: new Date().toISOString(),
            environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
        };

        console.log('[Amplitude] Track:', eventName, enrichedProps);
        amplitude.track(eventName, enrichedProps);
    }, [apiKey, shouldTrackEvent]);

    // Identify user and merge anonymous ID
    const identifyUser = useCallback((email: string, properties?: Record<string, any>) => {
        if (!apiKey || !email) return;

        // Set user ID - this merges anonymous events with identified user
        amplitude.setUserId(email);
        console.log('[Amplitude] User identified:', email);

        // Set user properties (always attach sticky funnel variant)
        const identify = new amplitude.Identify();
        Object.entries(properties || {}).forEach(([key, value]) => {
            identify.set(key, value);
        });
        identify.set('email', email);
        identify.set('funnel_variant', getFunnelVariant());
        identify.set('identified_at', new Date().toISOString());
        amplitude.identify(identify);
    }, [apiKey]);

    // ============================================
    // CONSOLIDATED EVENT TRACKING METHODS
    // ============================================

    const trackStepView = useCallback((props: StepViewProps) => {
        trackEventInternal(AMPLITUDE_EVENTS.STEP_VIEW, {
            step_name: props.step_name,
            step_index: props.step_index,
            total_steps: props.total_steps,
            flow_type: props.flow_type,
        });
    }, [trackEventInternal]);

    const trackStepCompleted = useCallback((props: StepCompletedProps) => {
        trackEventInternal(AMPLITUDE_EVENTS.STEP_COMPLETED, {
            step_name: props.step_name,
            flow_type: props.flow_type,
            time_spent_seconds: props.time_spent_seconds,
            ...props.selections,
        });
    }, [trackEventInternal]);

    const trackLead = useCallback((props: LeadProps) => {
        // First identify the user with their email
        identifyUser(props.email, {
            flow_type: props.flow_type,
            lead_source: 'onboarding',
            referral_code: props.referral_code,
        });

        // Then track the lead event
        trackEventInternal(AMPLITUDE_EVENTS.LEAD, {
            email: props.email,
            flow_type: props.flow_type,
            referral_code: props.referral_code,
        });
    }, [trackEventInternal, identifyUser]);

    const trackPaywallView = useCallback((flowType: FlowType) => {
        trackEventInternal(AMPLITUDE_EVENTS.PAYWALL_VIEW, {
            flow_type: flowType,
        });
    }, [trackEventInternal]);

    const trackCheckoutStarted = useCallback((props: PurchaseProps) => {
        trackEventInternal(AMPLITUDE_EVENTS.CHECKOUT_STARTED, {
            plan_type: props.plan_type,
            plan_id: props.plan_id,
            plan_name: props.plan_name,
            price: props.price,
            currency: props.currency || 'USD',
            flow_type: props.flow_type,
        });
    }, [trackEventInternal]);

    const trackPaymentSubmitted = useCallback((props: PaymentSubmittedProps) => {
        trackEventInternal(AMPLITUDE_EVENTS.PAYMENT_SUBMITTED, {
            plan_type: props.plan_type,
            plan_id: props.plan_id,
            plan_name: props.plan_name,
            price: props.price,
            currency: props.currency || 'USD',
            payment_method: props.payment_method,
            flow_type: props.flow_type,
            trial_promo: props.trial_promo,
        });
    }, [trackEventInternal]);

    const trackTrialPurchase = useCallback((props: PurchaseProps) => {
        trackEventInternal(AMPLITUDE_EVENTS.TRIAL_PURCHASE, {
            plan_type: props.plan_type,
            plan_id: props.plan_id,
            plan_name: props.plan_name,
            price: props.price,
            currency: props.currency || 'USD',
            flow_type: props.flow_type,
            trial_promo: props.trial_promo,
            plan_monthly_price: props.plan_monthly_price,
        });
    }, [trackEventInternal]);

    const trackUpsellPurchase = useCallback((props: UpsellProps) => {
        trackEventInternal(AMPLITUDE_EVENTS.UPSELL_PURCHASE, {
            product_type: props.product_type,
            price: props.price,
            flow_type: props.flow_type,
        });
    }, [trackEventInternal]);

    const trackFinalScreen = useCallback((flowType: FlowType) => {
        trackEventInternal(AMPLITUDE_EVENTS.FINAL_SCREEN, {
            flow_type: flowType,
        });
    }, [trackEventInternal]);

    // ============================================
    // USER PROPERTIES
    // ============================================

    const setUserProperties = useCallback((properties: Record<string, any>) => {
        if (!apiKey || !properties) return;
        const identify = new amplitude.Identify();
        Object.entries(properties || {}).forEach(([key, value]) => {
            identify.set(key, value);
        });
        amplitude.identify(identify);
    }, [apiKey]);

    const resetUser = useCallback(() => {
        if (!apiKey) return;
        amplitude.reset();
        sessionId.current = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }, [apiKey]);

    // Legacy trackEvent for backward compatibility
    const trackEvent = useCallback((eventName: string, eventProperties?: Record<string, any>) => {
        trackEventInternal(eventName, eventProperties || {});
    }, [trackEventInternal]);

    // Legacy trackPageView for backward compatibility
    const trackPageView = useCallback((pageName: string, properties?: Record<string, any>) => {
        trackEventInternal('Page Viewed', {
            page_name: pageName,
            page_path: window.location.pathname,
            page_url: window.location.href,
            ...properties,
        });
    }, [trackEventInternal]);

    // Legacy setUserId for backward compatibility
    const setUserId = useCallback((userId: string) => {
        if (!apiKey || !userId) return;
        amplitude.setUserId(userId);
        console.log('[Amplitude] User ID set:', userId);
    }, [apiKey]);

    return (
        <AmplitudeContext.Provider value={{
            // New consolidated API
            trackStepView,
            trackStepCompleted,
            trackLead,
            trackPaywallView,
            trackCheckoutStarted,
            trackPaymentSubmitted,
            trackTrialPurchase,
            trackUpsellPurchase,
            trackFinalScreen,

            // User management
            identifyUser,
            setUserProperties,
            resetUser,

            // Legacy (for backwards compatibility)
            trackEvent,
            trackPageView,
            setUserId,
        }}>
            {children}
        </AmplitudeContext.Provider>
    );
};

// No-op fallback when analytics are unavailable (e.g., provider crashed)
const noopAmplitude: AmplitudeContextType = {
    trackStepView: () => {},
    trackStepCompleted: () => {},
    trackLead: () => {},
    trackPaywallView: () => {},
    trackCheckoutStarted: () => {},
    trackPaymentSubmitted: () => {},
    trackTrialPurchase: () => {},
    trackUpsellPurchase: () => {},
    trackFinalScreen: () => {},
    identifyUser: () => {},
    setUserProperties: () => {},
    resetUser: () => {},
    trackEvent: () => {},
    trackPageView: () => {},
    setUserId: () => {},
};

// Custom hook to use Amplitude — returns no-ops if provider is unavailable
export const useAmplitude = (): AmplitudeContextType => {
    const context = useContext(AmplitudeContext);
    return context ?? noopAmplitude;
};
